
import StringUtils from "@/utils/StringUtils.js";
import ZonedClock from "./ZonedClock.js";

import RcMonth from "./RcMonth.js";
import RcDay from "./RcDay.js";
import RcDate from "./RcDate.js";

/**
 * The entry point to the time units, mirroring com.readychek.time.RcTime.
 *
 * An RcTime carries the zone every unit it builds is read in. Nothing on it changes that
 * zone as a side effect: at() used to reassign the instance's zone from whatever it was
 * handed, so an RcTime shared across a screen quietly started answering in someone else's
 * zone.
 */
export default class RcTime {

  static RC_PREFIX = "RC-";

  static Zone = {
    Names: {
      CANADA_TORONTO: "America/Toronto",
      CANADA_EDMONTON: "America/Edmonton",
      CANADA_VANCOUVER: "America/Vancouver",
      CANADA_HALIFAX: "America/Halifax",
      CANADA_MONTREAL: "America/Montreal",
      CANADA_ST_JOHNS: "America/St_Johns",
      CANADA_WINNIPEG: "America/Winnipeg",
      CANADA_REGINA: "America/Regina",
      // Saskatoon has no IANA zone of its own; America/Regina is the Saskatchewan zone.
      CANADA_SASKATOON: "America/Regina",
      CANADA_WHITEHORSE: "America/Whitehorse",
      CANADA_YELLOWKNIFE: "America/Yellowknife",
      CANADA_INUVIK: "America/Inuvik",
      CANADA_IQALUIT: "America/Iqaluit",
    },
  };

  /** A fixed clock, for tests and previews. Mirrors RcTime.setTime in java. */
  static currentTime = null;

  static setTime(timeUnit) {
    RcTime.currentTime = timeUnit;
  }

  static time(timeZone) {
    return new RcTime(timeZone);
  }

  constructor(timeZone) {
    this.withTimeZone(timeZone);
  }

  withTimeZone(timeZoneName) {
    this._timeZone = ZonedClock.safeZone(timeZoneName);
    return this;
  }

  timeZone() {
    return this._timeZone;
  }

  now() {
    if (RcTime.currentTime) {
      return new RcDate(RcTime.currentTime.date(), RcTime.currentTime.timeZone());
    }
    return new RcDate(new Date(), this._timeZone);
  }

  /** Now, read in the given zone rather than this clock's. */
  nowIn(zone) {
    if (RcTime.currentTime) {
      return new RcDate(RcTime.currentTime.date(), RcTime.currentTime.timeZone());
    }
    return new RcDate(new Date(), zone);
  }

  nowInNa() {
    return this.nowIn(RcTime.Zone.Names.CANADA_TORONTO);
  }

  today() {
    if (RcTime.currentTime) {
      return new RcDay(RcTime.currentTime.date(), RcTime.currentTime.timeZone());
    }
    return new RcDay(new Date(), this._timeZone);
  }

  isToday(compareTo) {
    const now = this.today().firstHour();
    const rcDate = compareTo.day().firstHour();
    return rcDate.time() === now.time();
  }

  /**
   * An instant, from a timestamp, a Date or another time unit. The zone defaults to this
   * clock's; passing one uses it for the returned date only.
   */
  at(time, tz = null) {
    const zone = tz ? ZonedClock.safeZone(tz) : this._timeZone;
    if (time && typeof time.time === "function") {
      return new RcDate(new Date(time.time()), tz ? zone : time.timeZone());
    }
    if (time instanceof Date) {
      return new RcDate(new Date(time.getTime()), zone);
    }
    return new RcDate(new Date(parseInt(StringUtils.toString(time), 10)), zone);
  }

  /**
   * A date built from its parts. The day is zero based, matching RcDay.value(), and the
   * hour is a 24 hour hour.
   */
  atParts(year, month, day, hour = 0, minute = 0, second = 0, timeZone = null) {
    const zone = timeZone ? ZonedClock.safeZone(timeZone) : this._timeZone;
    const timestamp = ZonedClock.timestampOf(
      { year, month, day: day + 1, hour, minute, second, milli: 0 }, zone,
    );
    return new RcDate(new Date(timestamp), zone);
  }

  /** The given month, anchored on its first day at midnight. */
  month(year, month) {
    const timestamp = ZonedClock.timestampOf(
      { year, month, day: 1, hour: 0, minute: 0, second: 0, milli: 0 }, this._timeZone,
    );
    return new RcMonth(new Date(timestamp), this._timeZone);
  }

  /** The given day at midnight. The day is zero based, matching RcDay.value(). */
  day(year, month, day) {
    return new RcDay(this.atParts(year, month, day).internalDate(), this._timeZone);
  }

  /** Parses a stored date: an RC- stamped string, a y-m-d string, or a timestamp. */
  fromString(value, zone) {
    const timeZone = zone ? ZonedClock.safeZone(zone) : this._timeZone;
    if (StringUtils.isEmpty(value)) {
      return new RcDate(new Date(0), timeZone);
    }
    if (StringUtils.startsWith(value, RcTime.RC_PREFIX)) {
      const parsed = Date.parse(value.substring(RcTime.RC_PREFIX.length).trim());
      return new RcDate(new Date(isNaN(parsed) ? 0 : parsed), timeZone);
    }
    if (value.indexOf("-") > -1 || value.indexOf("/") > -1) {
      const splitter = value.indexOf("/") > -1 ? "/" : "-";
      const parts = value.split(splitter);
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10) - 1;
      if (isNaN(year) || isNaN(month) || isNaN(day)) {
        return new RcDate(new Date(0), timeZone);
      }
      return this.atParts(year, month, day, 0, 0, 0, timeZone);
    }
    const timestamp = parseInt(value, 10);
    return new RcDate(new Date(isNaN(timestamp) ? 0 : timestamp), timeZone);
  }
}
